import { useState, useEffect } from 'react';
import { memberList, families as familiesApi } from '../api/endpoints';
import { getList } from '../api/client';
import type { Member, Family } from '../types';

// The member and family lists behind every picker.
//
// Six pickers can be mounted at once (a marriage form has two, a baptism form
// has a family and a member), and each one needs the whole directory to filter
// against. Fetching per picker would send the same request six times, so the
// list is loaded once and shared for the session — the same arrangement
// useChurchConfig uses for the letterhead.
//
// Both are light projections: the member summary leaves out photos and scanned
// documents, so this is a few hundred small rows, not megabytes.

interface Store<T> {
  cache: T[] | null;
  inFlight: Promise<T[]> | null;
  subscribers: Set<(rows: T[]) => void>;
  fetcher: () => Promise<T[]>;
}

const load = <T,>(store: Store<T>): Promise<T[]> => {
  if (store.cache) return Promise.resolve(store.cache);
  if (!store.inFlight) {
    store.inFlight = store.fetcher()
      .then((rows) => {
        store.cache = rows;
        return rows;
      })
      .catch(() => {
        // A picker that cannot load must not break the form around it: the
        // clerk falls back to typing the name, which every register allows.
        store.cache = [];
        return [] as T[];
      })
      .finally(() => { store.inFlight = null; });
  }
  return store.inFlight;
};

const refresh = <T,>(store: Store<T>): void => {
  store.cache = null;
  store.inFlight = null;
  load(store).then((rows) => store.subscribers.forEach((notify) => notify(rows)));
};

const useCachedList = <T,>(store: Store<T>) => {
  const [rows, setRows] = useState<T[]>(store.cache || []);
  const [loading, setLoading] = useState(!store.cache);

  useEffect(() => {
    let live = true;
    load(store).then((loaded) => {
      if (!live) return;
      setRows(loaded);
      setLoading(false);
    });

    const notify = (next: T[]) => { if (live) setRows(next); };
    store.subscribers.add(notify);
    return () => { live = false; store.subscribers.delete(notify); };
  }, [store]);

  return { rows, loading };
};

const memberStore: Store<Member> = {
  cache: null,
  inFlight: null,
  subscribers: new Set(),
  fetcher: () => memberList.summary().then((data) => getList<Member>(data))
};

const familyStore: Store<Family> = {
  cache: null,
  inFlight: null,
  subscribers: new Set(),
  fetcher: () => familiesApi.list().then((data) => getList<Family>(data))
};

/** Live members, for the register pickers. Voided rows are dropped. */
export const useMemberDirectory = () => {
  const { rows, loading } = useCachedList(memberStore);
  return { members: rows.filter((m) => m.recordStatus !== 'VOID'), loading };
};

/** Live families, for the "which household?" picker on a new child. */
export const useFamilyDirectory = () => {
  const { rows, loading } = useCachedList(familyStore);
  return { families: rows.filter((f) => f.recordStatus !== 'VOID'), loading };
};

/**
 * Re-reads the directory. Call after a register creates a member — a baptism
 * does — or the new child is missing from every picker until a page reload.
 */
export const invalidateDirectory = (): void => {
  refresh(memberStore);
  refresh(familyStore);
};
